/**
 * Versioned local save store (M5-A).
 *
 * Persistence choice: keep localStorage (documented MVP). Payload is small JSON;
 * IndexedDB deferred until multi-slot / larger saves need it. No account/cloud.
 *
 * - Migrates Milestone 4 v1 saves in place (never wipes valid progress).
 * - Quarantines corrupt payloads under rollbreak.save.corrupt.<ts>.
 * - Settlement is applied via settlement.ts (idempotent); this module only persists.
 */

import type { DungeonRunState } from '../domain/dungeon';
import { migrateToCurrent, SaveMigrationError } from './migrations';
import {
  SAVE_SCHEMA_VERSION,
  createDefaultPermanentSave,
  syncDungeonPermanentMirror,
  wrapDungeonAsRun,
  type PermanentSave,
  type RunState,
  type SavedGame,
} from './schema';

export { SAVE_SCHEMA_VERSION };

/** Same key as Milestone 4 so existing browser saves migrate in place. */
export const DEFAULT_SAVE_KEY = 'rollbreak.save.v1';

export const CORRUPT_SAVE_KEY_PREFIX = 'rollbreak.save.corrupt.';

export type LoadFailureReason =
  | 'missing'
  | 'corrupt'
  | 'unsupported_version'
  | 'invalid';

export type LoadResult =
  | { ok: true; game: SavedGame; migratedFrom?: number }
  | {
      ok: false;
      reason: LoadFailureReason;
      message?: string;
      quarantineKey?: string;
    };

export interface SaveStore {
  load(): SavedGame | null;
  loadDetailed(): LoadResult;
  save(game: SavedGame): void;
  clear(): void;
  hasSave(): boolean;
  /** Active dungeon for UI continue, or null. */
  activeDungeon(): DungeonRunState | null;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function memoryStorage(): StorageLike {
  const map = new Map<string, string>();
  return {
    getItem(key) {
      return map.has(key) ? map.get(key)! : null;
    },
    setItem(key, value) {
      map.set(key, value);
    },
    removeItem(key) {
      map.delete(key);
    },
  };
}

function browserStorage(): StorageLike {
  if (typeof localStorage === 'undefined') {
    return memoryStorage();
  }
  return localStorage;
}

function quarantineCorrupt(
  storage: StorageLike,
  raw: string,
  now: number = Date.now(),
): string {
  const key = `${CORRUPT_SAVE_KEY_PREFIX}${now}`;
  try {
    storage.setItem(key, raw);
  } catch {
    // Quota / private mode — still report failure without throwing.
  }
  return key;
}

function parseAndMigrate(raw: string): {
  game: SavedGame;
  migratedFrom?: number;
} {
  const parsed: unknown = JSON.parse(raw);
  const version =
    typeof parsed === 'object' &&
    parsed !== null &&
    'version' in parsed &&
    typeof (parsed as { version: unknown }).version === 'number'
      ? (parsed as { version: number }).version
      : undefined;
  const game = migrateToCurrent(parsed);
  return {
    game,
    migratedFrom: version !== undefined && version !== SAVE_SCHEMA_VERSION ? version : undefined,
  };
}

export function createSaveStore(
  key: string = DEFAULT_SAVE_KEY,
  storage: StorageLike = browserStorage(),
): SaveStore {
  const store: SaveStore = {
    loadDetailed(): LoadResult {
      const raw = storage.getItem(key);
      if (!raw) {
        return { ok: false, reason: 'missing' };
      }
      try {
        const { game, migratedFrom } = parseAndMigrate(raw);
        if (
          game.run &&
          (typeof game.rngState !== 'number' || !game.run.dungeon?.encounter)
        ) {
          const quarantineKey = quarantineCorrupt(storage, raw);
          return {
            ok: false,
            reason: 'invalid',
            message: 'Active run is missing encounter or rngState',
            quarantineKey,
          };
        }
        // Persist migrated shape so the next load is already current.
        if (migratedFrom !== undefined) {
          storage.setItem(
            key,
            JSON.stringify({ ...game, savedAt: Date.now(), version: SAVE_SCHEMA_VERSION }),
          );
        }
        return migratedFrom !== undefined
          ? { ok: true, game, migratedFrom }
          : { ok: true, game };
      } catch (err) {
        const quarantineKey = quarantineCorrupt(storage, raw);
        if (err instanceof SaveMigrationError) {
          const reason: LoadFailureReason = err.message.includes('Unsupported')
            ? 'unsupported_version'
            : 'invalid';
          return { ok: false, reason, message: err.message, quarantineKey };
        }
        return {
          ok: false,
          reason: 'corrupt',
          message: err instanceof Error ? err.message : 'JSON parse failed',
          quarantineKey,
        };
      }
    },

    load() {
      const result = this.loadDetailed();
      return result.ok ? result.game : null;
    },

    save(game) {
      const payload: SavedGame = {
        ...game,
        version: SAVE_SCHEMA_VERSION,
        savedAt: Date.now(),
      };
      storage.setItem(key, JSON.stringify(payload));
    },

    clear() {
      storage.removeItem(key);
    },

    hasSave() {
      return this.load() !== null;
    },

    activeDungeon() {
      const game = this.load();
      if (!game?.run) {
        return null;
      }
      if (game.run.status === 'settled') {
        return null;
      }
      return game.run.dungeon;
    },
  };
  return store;
}

export function createMemorySaveStore(
  key: string = DEFAULT_SAVE_KEY,
): SaveStore {
  return createSaveStore(key, memoryStorage());
}

export interface BuildSavedGameOptions {
  permanent?: PermanentSave;
  run?: RunState | null;
  /** Preserve an existing runId when updating the same run. */
  runId?: string;
}

/**
 * Builds a v2 SavedGame around the active dungeon (M4 call-site compatible).
 * Loads/creates permanent progress; wraps dungeon in RunState when needed.
 */
export function buildSavedGame(
  dungeon: DungeonRunState,
  rngState: number,
  options: BuildSavedGameOptions = {},
): SavedGame {
  const permanent =
    options.permanent ??
    createDefaultPermanentSave({
      unlockedDiceSlots: dungeon.unlockedDiceSlots,
    });
  const mirrored = syncDungeonPermanentMirror(dungeon, permanent);

  let run: RunState | null;
  if (options.run === null) {
    run = null;
  } else if (options.run) {
    run = {
      ...options.run,
      dungeon: mirrored,
      dungeonId: mirrored.dungeonId,
    };
  } else {
    run = wrapDungeonAsRun(mirrored, {
      runId: options.runId,
      status:
        mirrored.outcome === 'ongoing'
          ? 'active'
          : mirrored.phase === 'results'
            ? 'awaiting_settlement'
            : 'active',
    });
  }

  return {
    version: SAVE_SCHEMA_VERSION,
    savedAt: Date.now(),
    rngState,
    permanent,
    run,
  };
}

/**
 * Convenience: read dungeon from a loaded save (Continue button / tests).
 */
export function dungeonFromSave(game: SavedGame | null): DungeonRunState | null {
  return game?.run?.dungeon ?? null;
}

/**
 * Persist after settling without dropping permanent progress.
 */
export function buildSettledSavedGame(
  permanent: PermanentSave,
  run: RunState,
  rngState: number,
): SavedGame {
  return {
    version: SAVE_SCHEMA_VERSION,
    savedAt: Date.now(),
    rngState,
    permanent,
    run,
  };
}

/**
 * Update the active dungeon on an existing save without wiping permanent /
 * provisional run fields (Class XP, loot, settlement markers, runId).
 */
export function updateSavedGameRun(
  previous: SavedGame | null,
  dungeon: DungeonRunState,
  rngState: number,
): SavedGame {
  const permanent =
    previous?.permanent ??
    createDefaultPermanentSave({
      unlockedDiceSlots: dungeon.unlockedDiceSlots,
    });
  const mirrored = syncDungeonPermanentMirror(dungeon, permanent);
  if (previous?.run) {
    const status =
      previous.run.status === 'settled'
        ? previous.run.status
        : mirrored.outcome === 'ongoing'
          ? previous.run.status === 'awaiting_settlement'
            ? 'awaiting_settlement'
            : 'active'
          : mirrored.phase === 'results'
            ? 'awaiting_settlement'
            : previous.run.status;
    return buildSavedGame(mirrored, rngState, {
      permanent,
      run: {
        ...previous.run,
        status,
        dungeonId: mirrored.dungeonId,
        dungeon: mirrored,
      },
    });
  }
  return buildSavedGame(mirrored, rngState, { permanent });
}
